import React from 'react';
import { formatRM } from '../../lib/reportUtils';

/**
 * Side-by-side fee comparison table (Case A).
 * Ends with a TOTAL row that shows the projected sum over the remaining years.
 */
const FeeCompareTable = ({
  currentRows,
  recommendedRows,
  sumCurrent,
  sumRecommended,
  currentLabel,
  recommendedLabel,
  showHeadline = true,
}) => {
  // Intersection: years where BOTH sides have real fee data. Anything else
  // cannot honestly be turned into a "savings" figure.
  const overlap = recommendedRows
    .map((row, i) => ({ rec: row.fee, curr: currentRows[i]?.fee, year: row.year }))
    .filter((p) => p.rec != null && p.curr != null && p.rec > 0 && p.curr > 0);

  const overlapSumCurr = overlap.reduce((a, p) => a + p.curr, 0);
  const overlapSumRec  = overlap.reduce((a, p) => a + p.rec, 0);

  // Require the first two rendered years to have real rec fees. Without
  // near-term data the savings headline would be speculative.
  const firstTwoRecHaveData = recommendedRows.slice(0, 2)
    .every((r) => r.fee != null && r.fee > 0);
  const hasValidComparison = overlap.length >= 2 && firstTwoRecHaveData;

  const diff = hasValidComparison ? overlapSumCurr - overlapSumRec : 0;
  const isSaving = diff > 0;

  return (
    <div>
      {showHeadline && hasValidComparison && (
        <div
          className={`rounded-xl p-5 mb-5 ${
            isSaving ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
          }`}
          data-testid="fee-total-headline"
        >
          <p className="text-sm text-[#6B6560] mb-1">
            Projected total across {overlap.length} comparable year{overlap.length === 1 ? '' : 's'}
          </p>
          <p className={`text-3xl font-black ${isSaving ? 'text-emerald-700' : 'text-amber-700'}`}>
            {isSaving ? 'You would save ' : 'Additional cost of '}
            {formatRM(Math.abs(diff))}
          </p>
          <p className="text-xs text-[#6B6560] mt-1">
            only counting years where both schools publish fees
          </p>
        </div>
      )}

      {showHeadline && !hasValidComparison && (
        <div
          className="rounded-xl p-5 mb-5 bg-[#FFF5EB] border border-[#E8DED3]"
          data-testid="fee-compare-insufficient"
        >
          <p className="text-sm text-[#2C2823] font-semibold mb-1">
            Side-by-side savings not calculated
          </p>
          <p className="text-xs text-[#6B6560]">
            {recommendedLabel} hasn\u2019t published fees for the years your child will actually attend
            {' '}(or your current school\u2019s fees aren\u2019t on file). Contact admissions directly for a complete quote.
          </p>
        </div>
      )}

      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <table className="min-w-full text-sm border-separate border-spacing-0" data-testid="fee-compare-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#6B6560] align-top">
              <th className="py-2 pr-4 font-semibold w-14 align-top">Year</th>
              <th
                className="py-2 pr-4 font-semibold align-top whitespace-normal break-words leading-tight"
                data-testid="fee-table-current-header"
                title={currentLabel}
              >
                {currentLabel}
              </th>
              <th
                className="py-2 pr-4 font-semibold align-top whitespace-normal break-words leading-tight"
                data-testid="fee-table-recommended-header"
                title={recommendedLabel}
              >
                {recommendedLabel}
              </th>
              <th className="py-2 font-semibold align-top">Difference</th>
            </tr>
          </thead>
          <tbody>
            {recommendedRows
              .filter((row) => row.fee != null && row.fee > 0)
              .map((row) => {
                const originalIdx = recommendedRows.indexOf(row);
                const curr = currentRows[originalIdx]?.fee;
                const rec = row.fee;
                const rowDiff = curr != null && rec != null ? rec - curr : null;
                const diffClass =
                  rowDiff == null
                    ? 'text-[#9B8F88]'
                    : rowDiff > 0
                    ? 'text-amber-700'
                    : rowDiff < 0
                    ? 'text-emerald-700'
                    : 'text-[#6B6560]';
                return (
                  <tr key={row.year} className="border-t border-[#E8DED3]">
                    <td className="py-2.5 pr-4 font-semibold text-[#2C2823]">Y{row.year}</td>
                    <td className="py-2.5 pr-4 text-[#2C2823]">
                      {curr == null ? '\u2014' : Math.round(curr).toLocaleString()}
                      {currentRows[originalIdx]?.projected && curr != null && (
                        <span className="ml-1 text-xs text-[#9B8F88]">proj</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-[#2C2823]">
                      {Math.round(rec).toLocaleString()}
                      {row.projected && (
                        <span className="ml-1 text-xs text-[#9B8F88]">proj</span>
                      )}
                    </td>
                    <td className={`py-2.5 font-semibold ${diffClass}`}>
                      {rowDiff == null
                        ? '\u2014'
                        : `${rowDiff > 0 ? '+' : rowDiff < 0 ? '\u2212' : ''}${Math.abs(Math.round(rowDiff)).toLocaleString()}`}
                    </td>
                  </tr>
                );
              })}
            {hasValidComparison && (
              <tr className="border-t-2 border-[#2C2823] bg-[#FFF5EB]" data-testid="fee-total-row">
                <td className="py-3 pr-4 font-bold text-[#2C2823]">Total</td>
                <td className="py-3 pr-4 font-bold text-[#2C2823]">
                  RM {Math.round(overlapSumCurr).toLocaleString()}
                </td>
                <td className="py-3 pr-4 font-bold text-[#2C2823]">
                  RM {Math.round(overlapSumRec).toLocaleString()}
                </td>
                <td className={`py-3 font-bold ${isSaving ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {isSaving ? '\u2212' : '+'}RM {Math.round(Math.abs(diff)).toLocaleString()}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6B6560] mt-3 font-semibold">
        All fees in Malaysian Ringgit (RM).
      </p>
      <p className="text-xs text-[#9B8F88] mt-1">
        Fees indexed at 3% annual increase where actual figures aren't published. Registration and co-curricular levies excluded.
      </p>
    </div>
  );
};

export default FeeCompareTable;
